import { useCallback, useEffect, useRef, useState } from 'react';
import { ClipboardList, CheckCircle2, X } from 'lucide-react';
import type { PedidoSinConcluirVista } from '@shared/types';
import { IPC_CHANNELS } from '@shared/constants';
import { puedeUsarCanal } from '@shared/permisos';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { useStepUp } from '@renderer/hooks/useStepUp';
import { StepUpModal } from '@renderer/components/auth/StepUpModal';

/**
 * Sprint 164 — **"Quedaron N pedidos de días pasados sin concluir… ¿qué pasó con ellos?"**
 *
 * Texto de José (20/10/2026): "sin cobrar" sonaba a reclamo y podía hacerla dudar. El sistema no
 * sabe cómo terminó la historia de esos pedidos; ella sí. Por pedido, dos respuestas:
 *  - **Me lo pagaron** → se guarda con UN toque, en efectivo (23/10: con el paso "¿cómo te pagó?" no
 *    se guardó ninguno). Tarjeta o transferencia, con el enlace de abajo. Se anota en el día en que
 *    se vendió (lo decide el servidor).
 *  - **No se cobró** → se confirma una vez y queda cerrado sin cobro.
 * Y para toda la tarjeta: **No hacer nada por ahora** → una franja delgada, y mañana se vuelve a
 * preguntar.
 *
 * Va encima de "cerrar la caja de ayer" a propósito: si un pedido de ayer sí se pagó, primero se
 * anota y luego se cuenta; al revés, la caja diría que "sobra" justo ese dinero.
 */

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const fechaLarga = (iso: string) => {
  const d = new Date(iso);
  return `${DIAS[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()]}`;
};

const CLAVE_POSPUESTO = 'sin-concluir-pospuesto';
/** CerrarAyer escucha esto para volver a preguntar lo esperado: un cobro de ayer lo cambia. */
export const EVENTO_SIN_CONCLUIR_RESUELTO = 'pos:sin-concluir-resuelto';
/**
 * Mientras esta tarjeta pregunta, la de la caja de ayer espera (revisión ux-master S164): dos
 * diálogos encimados se pelean el foco, y el orden correcto es anotar primero y contar después.
 */
export const EVENTO_SIN_CONCLUIR_ABIERTA = 'pos:sin-concluir-abierta';

type Metodo = 'efectivo' | 'tarjeta' | 'transferencia';
type Paso = { pedidoId: number; que: 'metodo' | 'confirmar-no' } | null;

export function PedidosSinConcluir() {
  const rol = useSessionStore((s) => s.sesion?.rol);
  const puede = puedeUsarCanal(rol, IPC_CHANNELS.SIN_CONCLUIR_LISTAR);
  const simbolo = useNegocioStore((s) => s.negocio?.monedaSimbolo ?? '$');
  const [lista, setLista] = useState<PedidoSinConcluirVista[]>([]);
  const [abierta, setAbierta] = useState(false);
  const [paso, setPaso] = useState<Paso>(null);
  const [enviando, setEnviando] = useState(false);
  // Revisión ux-master S164: el error va en la tarjeta del pedido que falló, no arriba de la lista.
  const [error, setError] = useState<{ pedidoId: number; texto: string } | null>(null);
  // Y lo que se anotó se dice: en una acción de dinero, que la tarjeta sólo desaparezca no basta.
  const [aviso, setAviso] = useState<string | null>(null);
  const [pulsado, setPulsado] = useState<string | null>(null);
  const [hechos, setHechos] = useState(0);
  const enviandoRef = useRef(false);
  const stepUp = useStepUp<{ pedidoId: number; resultado: 'pagado' | 'no_cobrado' }>();
  const $ = (c: number) => formatPrecio(c, simbolo);

  const consultar = useCallback(async () => {
    const r = await window.api.sinConcluir.listar();
    if (!r.ok) return; // si falla, no se interrumpe a nadie: se vuelve a preguntar después
    setLista(r.data);
    if (r.data.length > 0) setAbierta(localStorage.getItem(CLAVE_POSPUESTO) !== new Date().toDateString());
  }, []);

  useEffect(() => {
    if (!puede) return;
    void consultar();
    const alVolver = () => {
      if (document.visibilityState === 'visible') void consultar();
    };
    document.addEventListener('visibilitychange', alVolver);
    const cada = window.setInterval(() => void consultar(), 30 * 60_000);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.clearInterval(cada);
    };
  }, [puede, consultar]);

  /*
    Revisión ux-master S164 (bloqueante, medido con Playwright): el foco se quedaba en la página de
    atrás. Tab recorría los botones tapados ("Entendido", "Mi PIN", "Salir"…) y Escape no hacía nada,
    porque la tarjeta nunca recibía el foco. Al abrir, al cambiar de paso y al resolver un pedido, el
    foco vuelve a la tarjeta; nunca mientras la clave está pidiéndose.
  */
  const tarjetaRef = useRef<HTMLDivElement>(null);
  const hayPregunta = puede && abierta && lista.length > 0;
  useEffect(() => {
    if (!hayPregunta || stepUp.modalAbierto) return;
    const t = tarjetaRef.current;
    if (!t) return;
    if (paso) t.querySelector<HTMLElement>(`[data-panel="${paso.pedidoId}"]`)?.focus();
    else if (!t.contains(document.activeElement)) t.focus();
  }, [hayPregunta, paso, lista.length, stepUp.modalAbierto]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(EVENTO_SIN_CONCLUIR_ABIERTA, { detail: hayPregunta }));
  }, [hayPregunta]);
  useEffect(() => () => void window.dispatchEvent(new CustomEvent(EVENTO_SIN_CONCLUIR_ABIERTA, { detail: false })), []);

  if (!puede) return null;
  if (lista.length === 0 && hechos === 0) return null;

  const posponer = () => {
    localStorage.setItem(CLAVE_POSPUESTO, new Date().toDateString());
    setAbierta(false);
    setPaso(null);
    setError(null);
    setAviso(null);
    setHechos(0);
  };

  const elegir = (nuevo: Paso) => {
    setPaso(nuevo);
    setError(null);
    setAviso(null);
  };

  const resolver = async (pedidoId: number, resultado: 'pagado' | 'no_cobrado', metodo?: Metodo) => {
    if (enviandoRef.current) return; // el freno va ANTES del await (lección del Sprint 159)
    enviandoRef.current = true;
    setEnviando(true);
    setPulsado(`${pedidoId}:${metodo ?? 'no'}`);
    setError(null);
    setAviso(null);
    const r = await stepUp.ejecutar(() => window.api.sinConcluir.resolver({ pedidoId, resultado, metodo }));
    enviandoRef.current = false;
    setEnviando(false);
    setPulsado(null);
    if (r.ok) {
      const p = lista.find((x) => x.pedidoId === pedidoId);
      const num = `#${String(p?.numeroPedido ?? '').padStart(5, '0')}`;
      setAviso(
        resultado === 'pagado'
          ? `Listo: el pedido ${num} quedó pagado en ${metodo} (${$(p?.total ?? 0)}).`
          : `Listo: el pedido ${num} quedó cerrado sin cobro.`,
      );
      const quedan = lista.filter((x) => x.pedidoId !== pedidoId);
      setLista(quedan);
      setHechos((n) => n + 1);
      setPaso(null);
      window.dispatchEvent(new Event(EVENTO_SIN_CONCLUIR_RESUELTO));
      // Auditoría `cyber` S164: si había más de los que caben en una lista, no se dice "ya no queda nada".
      if (quedan.length === 0) void consultar();
      return;
    }
    if (r.codigo !== 'STEP_UP_REQUIRED' && r.codigo !== 'CANCELADO') {
      setError({ pedidoId, texto: traducirErrorBackend(r.error, r.codigo) });
      void consultar();
    }
  };
  const etiqueta = (clave: string, texto: string) => (pulsado === clave ? 'Anotando…' : texto);

  // ------------------------------------------------------------------ franja (pospuesta)
  if (!abierta) {
    if (lista.length === 0) return null;
    return (
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 py-1 pl-4 pr-1">
        <ClipboardList className="h-5 w-5 text-amber-700" aria-hidden />
        <span className="flex-1 text-sm font-medium text-amber-900">
          {lista.length === 1 ? 'Hay 1 pedido de días pasados sin concluir' : `Hay ${lista.length} pedidos de días pasados sin concluir`}
        </span>
        <button
          className="min-h-[44px] rounded-lg bg-amber-700 px-4 text-sm font-semibold text-white hover:bg-amber-800"
          data-uso="pedidos:sin-concluir-abrir"
          onClick={() => setAbierta(true)}
        >
          Revisarlos
        </button>
      </div>
    );
  }

  // ------------------------------------------------------------------ todo resuelto
  if (lista.length === 0) {
    return (
      <div className="fixed inset-0 z-[81] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="sin-concluir-listo">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
          <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" aria-hidden />
          <h2 id="sin-concluir-listo" className="mt-2 text-2xl font-bold text-slate-900">Listo, ya no queda nada pendiente de días pasados</h2>
          <button autoFocus className="btn-primary btn-lg mt-6 min-h-[48px] w-full" onClick={() => setHechos(0)}>
            Listo
          </button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------ la pregunta
  return (
    <>
      <div
        className="fixed inset-0 z-[81] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sin-concluir-titulo"
        onKeyDown={(e) => {
          if (e.key === 'Escape') posponer();
          if (e.key !== 'Tab') return;
          // Tab da la vuelta dentro de la tarjeta: lo de atrás está tapado.
          const f = [...(tarjetaRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])];
          if (f.length === 0) return;
          const actual = document.activeElement;
          if (e.shiftKey && (actual === f[0] || actual === tarjetaRef.current)) {
            e.preventDefault();
            f[f.length - 1].focus();
          } else if (!e.shiftKey && actual === f[f.length - 1]) {
            e.preventDefault();
            f[0].focus();
          }
        }}
      >
        <div ref={tarjetaRef} tabIndex={-1} className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl outline-none">
          <button
            className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
            onClick={posponer}
            aria-label="No hacer nada por ahora"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="flex items-center gap-3 pr-10">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <ClipboardList className="h-7 w-7" aria-hidden />
            </div>
            <h2 id="sin-concluir-titulo" className="text-xl font-bold text-slate-900">
              {lista.length === 1 ? 'Quedó 1 pedido de días pasados sin concluir' : `Quedaron ${lista.length} pedidos de días pasados sin concluir`}
            </h2>
          </div>
          <p className="mt-2 text-lg text-slate-800">¿Qué pasó con {lista.length === 1 ? 'él' : 'ellos'}?</p>

          <div role="status" aria-live="polite">
            {aviso && (
              <p className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-base font-medium text-emerald-800">
                <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden />
                {aviso}
              </p>
            )}
          </div>

          <ul className="mt-4 max-h-[55vh] space-y-3 overflow-y-auto pr-1">
            {lista.map((p) => {
              const activo = paso?.pedidoId === p.pedidoId ? paso.que : null;
              return (
                <li key={p.pedidoId} className="rounded-xl border-2 border-slate-200 p-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-semibold text-slate-900">
                      Pedido #{String(p.numeroPedido).padStart(5, '0')}
                      {p.mesa !== null && <span className="font-normal text-slate-700"> · Mesa {p.mesa}</span>}
                    </p>
                    <p className="text-lg font-bold text-slate-900">{$(p.total)}</p>
                  </div>
                  <p className="text-sm text-slate-700">
                    {fechaLarga(p.fecha)} · {p.entregado ? 'se entregó' : 'no se marcó como entregado'}
                  </p>
                  <p className="mt-1 text-sm text-slate-800">{p.renglones.join(', ')}</p>

                  {/*
                    Incidente 23/10/2026 — Claudia tocó "Me lo pagaron" 12 veces y no se guardó ninguna: el
                    botón sólo abría "¿Cómo te pagó?" y ella pasaba al siguiente pedido creyendo que ya había
                    contestado. Ahora "Me lo pagaron" guarda en el acto, en efectivo (como le pagan casi
                    siempre); tarjeta o transferencia se eligen aparte, ANTES de guardar.
                  */}
                  {activo === null && (
                    <>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button
                          className="min-h-[52px] rounded-xl bg-primary px-2 font-semibold leading-tight text-white hover:opacity-90 disabled:opacity-60"
                          disabled={enviando}
                          data-uso="pedidos:sin-concluir-pagado"
                          onClick={() => void resolver(p.pedidoId, 'pagado', 'efectivo')}
                        >
                          {pulsado === `${p.pedidoId}:efectivo` ? (
                            'Anotando…'
                          ) : (
                            <>
                              Me lo pagaron
                              <span className="block text-sm font-normal">en efectivo</span>
                            </>
                          )}
                        </button>
                        <button
                          className="min-h-[52px] rounded-xl border-2 border-slate-500 font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                          disabled={enviando}
                          data-uso="pedidos:sin-concluir-no-cobro"
                          onClick={() => elegir({ pedidoId: p.pedidoId, que: 'confirmar-no' })}
                        >
                          No se cobró
                        </button>
                      </div>
                      <button
                        className="mt-1 min-h-[44px] w-full rounded-lg text-sm font-semibold text-primary underline underline-offset-2 hover:bg-primary/5 disabled:opacity-60"
                        disabled={enviando}
                        data-uso="pedidos:sin-concluir-otro-metodo"
                        onClick={() => elegir({ pedidoId: p.pedidoId, que: 'metodo' })}
                      >
                        ¿Te pagó con tarjeta o transferencia?
                      </button>
                    </>
                  )}

                  {activo === 'metodo' && (
                    <div className="mt-3">
                      <p className="font-semibold text-slate-900 outline-none" data-panel={p.pedidoId} tabIndex={-1}>¿Cómo te pagó?</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {(['tarjeta', 'transferencia'] as const).map((m) => (
                          <button
                            key={m}
                            className="min-h-[48px] rounded-xl border-2 border-primary bg-white px-1 text-sm font-semibold capitalize text-primary hover:bg-primary/5 disabled:opacity-60"
                            disabled={enviando}
                            onClick={() => void resolver(p.pedidoId, 'pagado', m)}
                          >
                            {etiqueta(`${p.pedidoId}:${m}`, m)}
                          </button>
                        ))}
                      </div>
                      <button className="mt-2 min-h-[44px] w-full rounded-xl font-semibold text-slate-700 hover:bg-slate-100" disabled={enviando} onClick={() => setPaso(null)}>
                        Regresar
                      </button>
                    </div>
                  )}

                  {activo === 'confirmar-no' && (
                    <div className="mt-3 rounded-lg bg-amber-50 p-3">
                      <p className="text-base font-medium text-amber-900 outline-none" data-panel={p.pedidoId} tabIndex={-1}>Queda cerrado sin cobro. Ya no se puede cobrar después.</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button
                          className="min-h-[48px] rounded-xl bg-amber-700 font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
                          disabled={enviando}
                          onClick={() => void resolver(p.pedidoId, 'no_cobrado')}
                        >
                          {etiqueta(`${p.pedidoId}:no`, 'Sí, no se cobró')}
                        </button>
                        <button className="min-h-[48px] rounded-xl border-2 border-slate-500 bg-white font-semibold text-slate-800 hover:bg-slate-50" disabled={enviando} onClick={() => setPaso(null)}>
                          Regresar
                        </button>
                      </div>
                    </div>
                  )}

                  {error?.pedidoId === p.pedidoId && (
                    <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                      {error.texto}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>

          <button
            className="mt-4 min-h-[48px] w-full rounded-xl border-2 border-slate-500 font-semibold text-slate-800 hover:bg-slate-100"
            data-uso="pedidos:sin-concluir-despues"
            onClick={posponer}
          >
            No hacer nada por ahora
          </button>
        </div>
      </div>
      <StepUpModal abierto={stepUp.modalAbierto} onConfirmado={stepUp.onConfirmado} onCancelar={stepUp.onCancelar} />
    </>
  );
}
